import { useState, useEffect } from 'react';
import { InputRange, type Range } from '@bygghemmait/components/form';
import { RangeFacetFilter } from 'models/RangeFacetFilter';
import { FilterProps } from '../mobile/types';
import styles from './simpleSlider.scss';
import connect from 'components/Shared/connect';
import { filterRange } from 'components/Filter/actions';
import { filterChanged } from 'views/ProductlistPage/actions';
import { FilterHeader, FilterHeaderProps } from '../mobile/filterHeader';
import cn from 'classnames';
import { isDesktopLayout } from 'components/Shared/responsive';
import { Input } from 'components/Shared/Form/themes/BGH-RD3/input';
import { NO_ELEMENT } from 'utils';
import { priceFormat } from 'components/Shared/Price';
import { disableInput } from './utils/disableInput';
import { getUnit, getUnitToShow } from './utils/unit';
import { useInputChange } from './utils/useInputChange';
import { forceMaxValue, forceMinValue, setValue } from './utils/setValue';
import { useSelector } from 'react-redux';
import { getCurrencySymbol } from 'store/appShell/selector';

export type OwnProps = FilterProps & RangeFacetFilter;
interface MergedProps extends OwnProps {
  onChangeCompleted: (value: Range) => void;
  value: Range;
  isDesktop?: boolean;
}

interface SliderValue {
  min: number | string;
  max: number | string;
}

interface HeaderProps {
  noHeader?: boolean;
  isOpen: boolean;
  toggleOpen: () => void;
  active: boolean;
  min: number;
  max: number;
  unit: string;
}
const Header = ({
  noHeader,
  isOpen,
  displayName,
  toggleOpen,
  active,
  unit,
  min,
  max
}: FilterHeaderProps & HeaderProps) => {
  const _min = priceFormat({ price: min });
  const _max = priceFormat({ price: max });

  if (noHeader) {
    return NO_ELEMENT;
  }

  return (
    <FilterHeader border={!isOpen} displayName={displayName} onClick={toggleOpen}>
      {(isOpen || active) && (
        <span>
          {_min} - {_max} {unit}
        </span>
      )}
    </FilterHeader>
  );
};

const _SimpleSliderFilter = ({
  displayName,
  name,
  toggleOpen,
  isOpen,
  active,
  onChangeCompleted,
  maxValue,
  minValue,
  value,
  noHeader,
  isDesktop,
  ...props
}: MergedProps) => {
  const [localValue, _setLocalValue] = useState<SliderValue>(value);
  const [{ rangeMin, rangeMax }, setRange] = useState({
    rangeMin: Math.floor(props.rangeMin || 0),
    rangeMax: Math.ceil(props.rangeMax || 0)
  });
  const currencySymbol = useSelector(getCurrencySymbol);

  const setLocalValue = (value: Range): void => {
    const min = Math.floor(rangeMin || minValue);
    const max = Math.ceil(rangeMax || maxValue);

    if (value.max <= max && value.min >= min) {
      _setLocalValue(value);
    }
  };

  useEffect(() => {
    if (!active) {
      setRange({ rangeMin: props.rangeMin || minValue, rangeMax: props.rangeMax || maxValue });
      _setLocalValue({
        min: Math.floor(rangeMin || minValue),
        max: Math.ceil(rangeMax || maxValue)
      });
    }
  }, [active, maxValue, minValue, rangeMax, rangeMin, props.rangeMax, props.rangeMin]);

  const { min, max } = localValue;

  const inputChange = useInputChange({
    onChangeComplete: onChangeCompleted,
    setLocalValue: _setLocalValue,
    maxValue,
    minValue
  });

  const unit = getUnit({ name, displayName, currencySymbol });
  return (
    <>
      <Header
        noHeader={noHeader || isDesktop}
        displayName={displayName}
        toggleOpen={toggleOpen}
        isOpen={isOpen}
        active={active}
        min={getValidNumber(min)}
        max={getValidNumber(max)}
        unit={unit}
      />
      <div
        className={cn(styles.sliderTextContainer, {
          [styles.desktopSlider]: isDesktop,
          [styles.close]: !isOpen && !isDesktop
        })}
      >
        <Input
          className={cn(styles.input, styles.spacing)}
          type="number"
          value={min}
          unit={getUnitToShow({ unit, value: min })}
          onKeyDown={disableInput}
          onBlur={forceMinValue({
            inputChange,
            max,
            min,
            minValue
          })}
          onChange={setValue({ inputChange, max })}
          inputMode="numeric"
        />
        <Input
          className={styles.input}
          type="number"
          value={max}
          onKeyDown={disableInput}
          unit={getUnitToShow({ unit, value: max })}
          onBlur={forceMaxValue({
            inputChange,
            max,
            min,
            maxValue
          })}
          onChange={setValue({ inputChange, min })}
          inputMode="numeric"
        />
      </div>
      <div
        className={cn(styles.simpleSlider, {
          [styles.desktopSlider]: isDesktop,
          [styles.close]: !isOpen && !isDesktop
        })}
      >
        <InputRange
          minValue={Math.floor(rangeMin || minValue)}
          maxValue={Math.ceil(rangeMax || maxValue)}
          value={{
            min: getValidNumber(localValue.min),
            max: getValidNumber(localValue.max)
          }}
          bounds={{
            max: maxValue,
            min: minValue
          }}
          onChangeEnd={onChangeCompleted}
          onChange={setLocalValue}
        />
      </div>
    </>
  );
};

const getValidNumber = (input: number | string): number => {
  if (!input) {
    return 0;
  }

  return +input;
};

export const SimpleSliderFilter = connect<MergedProps, OwnProps>(
  state => ({
    isDesktop: isDesktopLayout(state)
  }),
  (dispatch, { name, selectedMin, selectedMax }: MergedProps) => ({
    onChangeCompleted: (value: Range) => {
      const action = { facet: name, value };
      dispatch(filterRange(action));
      void dispatch(filterChanged(action));
    },
    value: { min: selectedMin, max: selectedMax }
  })
)(_SimpleSliderFilter);
